import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';


interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  is_active: boolean;
  created_at: string;
}

const UserAdministration: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    role: 'user' as 'admin' | 'user'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/auth/users`);
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API_URL}/api/auth/users`, formData);
      setShowCreateModal(false);
      setFormData({ username: '', email: '', name: '', password: '', role: 'user' });
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/auth/users/${userId}`);
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await axios.put(`${API_URL}/api/auth/users/${user.id}`, {
        is_active: !user.is_active
      });
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update user');
    }
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-sans font-bold normal-case text-gray-800 dark:text-white">User Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-400 hover:bg-green-500 text-gray-800 px-4 py-2 rounded font-sans font-semibold transition"
        >
          Create User
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-500">
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Username</th>
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Name</th>
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Email</th>
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Role</th>
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Status</th>
              <th className="text-left py-3 px-4 font-semibold font-sans text-gray-800 dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-gray-100 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="py-3 px-4 font-sans text-sm text-gray-800 dark:text-white">{user.username}</td>
                <td className="py-3 px-4 font-sans text-gray-800 dark:text-white">{user.name}</td>
                <td className="py-3 px-4 text-sm font-sans text-gray-600 dark:text-gray-300">{user.email}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded text-xs font-sans font-semibold ${
                    user.role === 'admin' ? 'bg-purple-200 dark:bg-purple-300 text-gray-800' : 'bg-blue-200 dark:bg-blue-300 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded text-xs font-sans font-semibold ${
                    user.is_active ? 'bg-green-300 text-gray-800' : 'bg-orange-200 text-gray-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className="text-xs font-sans px-2 py-1 bg-gray-200 dark:bg-gray-500 hover:bg-gray-300 dark:hover:bg-gray-400 text-gray-800 dark:text-white rounded"
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-xs font-sans px-2 py-1 bg-red-400 hover:bg-red-500 text-gray-800 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-sans font-bold normal-case text-gray-800 dark:text-white mb-4">Create New User</h3>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-white mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-white mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-white mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-white mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-white mb-2">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'user' })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-400 hover:bg-blue-500 text-gray-800 font-sans font-semibold py-2 rounded transition"
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ username: '', email: '', name: '', password: '', role: 'user' });
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-500 hover:bg-gray-400 dark:hover:bg-gray-400 text-gray-800 dark:text-white font-sans font-semibold py-2 rounded transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdministration;
